// frontend/src/pages/admin/AdminSettings.js
import React, { useState, useEffect } from 'react';
import { settingsApi } from '../../api/settings';
import { adminApi } from '../../api/admin';
import { bannersApi } from '../../api/banners';
import uploadService from '../../services/upload';
import { compressImage } from '../../utils/imageCompression'; 
import { 
    FaSave, FaCogs, FaMoneyBillWave, FaSpinner, 
    FaPlus, FaTrash, FaTimes, 
    FaBullhorn, FaListAlt, FaHeartbeat, 
    FaPlay, FaPause, FaEdit, FaInfoCircle, 
    FaCommentDots, FaCheck, FaMoneyCheckAlt, FaTools, 
    FaCamera, FaGem, FaRocket, FaBan, FaCalendarAlt, 
    FaPalette, FaLink, FaUndo, FaRunning, FaClock
} from 'react-icons/fa';

function AdminSettings() {
  const [activeTab, setActiveTab] = useState('banners');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // --- 1. CONFIGURATION STATE ---
  const [pricing, setPricing] = useState({});
  const [premiumConfig, setPremiumConfig] = useState({}); 
  const [mediaConfig, setMediaConfig] = useState({}); 
  const [payoutConfig, setPayoutConfig] = useState({}); 
  const [siteContent, setSiteContent] = useState({}); 
  const [serviceConfig, setServiceConfig] = useState({});
  
  // --- 2. SMS STATE (UPDATED FOR GRANULAR CONTROL) ---
  const [smsConfig, setSmsConfig] = useState({
      active_otp_provider: 'termii',
      active_sms_provider: 'kudisms',
      active_email_provider: 'google',
      termii_api_key: '',
      termii_sender_id: '',
      kudi_api_key: '',
      kudi_sender_id: ''
  });
  const [smsBalance, setSmsBalance] = useState(null);
  const [loadingBalance, setLoadingBalance] = useState(false);

  // --- 3. SYSTEM STATE ---
  const [health, setHealth] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);

  // --- 4. BANNER STATE ---
  const [banners, setBanners] = useState([]);
  const [isBannerModalOpen, setIsBannerModalOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [bannerForm, setBannerForm] = useState({
      text: '',
      image_url: '',
      link_url: '',
      background_color: '#2563EB',
      text_color: '#FFFFFF',
      font_size: 'medium',
      animation_mode: 'static',
      animation_speed: 15, 
      start_date: '',
      end_date: '',
      is_active: true
  });

  // --- INITIAL LOAD ---
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- LAZY LOAD TABS ---
  useEffect(() => {
      if (activeTab === 'health') fetchHealth();
      if (activeTab === 'logs') fetchLogs();
      if (activeTab === 'banners') fetchBanners();
      if (activeTab === 'sms') fetchSmsBalance();
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
        const settingsData = await settingsApi.getAllSettings();
        setPricing(settingsData.pricing || {});
        setPremiumConfig(settingsData.premium_config || {}); 
        setMediaConfig(settingsData.media_config || {}); 
        setPayoutConfig(settingsData.payout_config || {}); 
        setSmsConfig(settingsData.sms_config || { active_otp_provider: 'termii', active_sms_provider: 'kudisms', active_email_provider: 'google' });
        setServiceConfig(settingsData.service_config || {});
        
        const contentData = await settingsApi.getSiteContent();
        setSiteContent(contentData || {});

        fetchBanners();

    } catch (e) { console.error("Error fetching settings:", e); }
    setLoading(false);
  };

  const fetchHealth = async () => {
      try { const data = await adminApi.getSystemHealth(); setHealth(data); } catch (e) {}
  };

  const fetchLogs = async () => {
      try { const data = await adminApi.getAuditLogs(); setAuditLogs(data); } catch (e) {}
  };

  const fetchBanners = async () => {
      try { const data = await bannersApi.getAllBannersAdmin(); setBanners(data); } catch (e) {}
  };

  const fetchSmsBalance = async () => {
      setLoadingBalance(true);
      try {
          const token = localStorage.getItem('accessToken');
          const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/settings/sms-balance`, {
              headers: { 'Authorization': `Bearer ${token}` }
          });
          const data = await response.json();
          setSmsBalance(data);
      } catch (e) { console.error("Balance fetch failed", e); } 
      finally { setLoadingBalance(false); }
  };

  // --- GENERIC HANDLERS ---
  const handleConfigChange = (setter) => (e) => {
      const { name, value, type, checked } = e.target;
      const val = type === 'checkbox' ? checked : value;
      const finalVal = type === 'number' && val !== '' ? parseFloat(val) : val;
      setter(prev => ({ ...prev, [name]: finalVal }));
  };

  const saveConfig = async (category, data, customMessage = null) => {
      setSaving(true);
      try {
          const cleanedData = { ...data };
          await settingsApi.updateSettings(category, cleanedData);
          alert(customMessage || `${category.replace('_', ' ').toUpperCase()} updated successfully!`);
          if (category === 'sms_config') fetchSmsBalance();
      } catch (err) {
          alert(`Failed to save ${category}: ${err.message}`);
      } finally {
          setSaving(false);
      }
  };

  // --- IMAGE UPLOAD HANDLER ---
  const handleImageUpload = async (e, section) => {
      const file = e.target.files[0];
      if (!file) return;
      setSaving(true);
      try {
          const compressionQuality = mediaConfig.hero_compression || 0.6;
          const compressed = await compressImage(file, compressionQuality, 1920, 1080);
          const response = await uploadService.uploadImage(compressed, section);
          
          if (section === 'site-content') {
              setSiteContent(prev => ({ ...prev, hero_image_url: response.url }));
              await settingsApi.updateSiteContent({ hero_image_url: response.url });
          }
          alert("Image uploaded successfully!");
      } catch (err) { alert("Upload failed."); } 
      finally { setSaving(false); }
  };

  // --- BANNER HANDLERS ---
  const handleOpenBannerModal = (banner = null) => {
      if (banner) {
          setEditingBanner(banner);
          setBannerForm({
              text: banner.text,
              image_url: banner.image_url || '',
              link_url: banner.link_url || '',
              background_color: banner.background_color || '#2563EB',
              text_color: banner.text_color || '#FFFFFF',
              font_size: banner.font_size || 'medium',
              animation_mode: banner.animation_mode || 'static',
              animation_speed: banner.animation_speed || 15,
              start_date: banner.start_date || '',
              end_date: banner.end_date || '',
              is_active: banner.is_active
          });
      } else {
          setEditingBanner(null);
          const now = new Date();
          const nextWeek = new Date();
          nextWeek.setDate(now.getDate() + 7);
          const format = (d) => d.toISOString().slice(0, 16);
          
          setBannerForm({
              text: '',
              image_url: '',
              link_url: '',
              background_color: '#2563EB',
              text_color: '#FFFFFF',
              font_size: 'medium',
              animation_mode: 'static',
              animation_speed: 15,
              start_date: format(now),
              end_date: format(nextWeek),
              is_active: true
          });
      }
      setIsBannerModalOpen(true);
  };

  const handleBannerChange = (e) => {
      const { name, value, type, checked } = e.target;
      setBannerForm(prev => ({
          ...prev,
          [name]: type === 'checkbox' ? checked : value
      }));
  };

  const setBannerImmediately = (e) => {
      e.preventDefault();
      setBannerForm(prev => ({ ...prev, start_date: new Date().toISOString().slice(0, 16) }));
  };

  const handleSaveBanner = async (e) => {
      e.preventDefault();
      setSaving(true);
      try {
          if (editingBanner) {
              await bannersApi.updateBanner(editingBanner.id, bannerForm);
          } else {
              await bannersApi.createBanner(bannerForm);
          }
          setIsBannerModalOpen(false);
          fetchBanners();
      } catch (err) {
          alert("Failed to save banner: " + (err.response?.data?.message || err.message));
      } finally {
          setSaving(false);
      }
  };

  const handleDeleteBanner = async (id) => {
      if (!window.confirm("Delete this banner schedule?")) return;
      try {
          await bannersApi.deleteBanner(id);
          fetchBanners();
      } catch (err) { alert("Failed to delete banner."); }
  };

  const toggleBannerStatus = async (banner) => {
      try {
          await bannersApi.updateBanner(banner.id, { ...banner, is_active: !banner.is_active });
          fetchBanners();
      } catch (err) { alert("Failed to update status."); }
  };

  const getBannerStatus = (banner) => {
      const now = new Date().toISOString();
      if (!banner.is_active) return <span className="bg-red-100 text-red-700 px-2 py-1 rounded text-xs font-bold">Disabled</span>;
      if (now < banner.start_date) return <span className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded text-xs font-bold">Scheduled</span>;
      if (now > banner.end_date) return <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs font-bold">Expired</span>;
      return <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-bold animate-pulse">Active Now</span>;
  };

  return (
    <div className="pb-20">
      <h1 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
        <FaCogs className="text-primary" /> System Configuration
      </h1>

      {/* --- TABS NAVIGATION --- */}
      <div className="flex gap-4 mb-6 border-b border-gray-200 overflow-x-auto whitespace-nowrap pb-2">
        <button onClick={() => setActiveTab('banners')} className={`pb-2 px-4 font-medium flex items-center gap-2 ${activeTab === 'banners' ? 'border-b-2 border-primary text-primary' : 'text-gray-500'}`}>
          <FaBullhorn /> Banners
        </button>
        <button onClick={() => setActiveTab('pricing')} className={`pb-2 px-4 font-medium flex items-center gap-2 ${activeTab === 'pricing' ? 'border-b-2 border-primary text-primary' : 'text-gray-500'}`}>
          <FaMoneyBillWave /> Pricing
        </button>
        <button onClick={() => setActiveTab('premium')} className={`pb-2 px-4 font-medium flex items-center gap-2 ${activeTab === 'premium' ? 'border-b-2 border-primary text-primary' : 'text-gray-500'}`}>
          <FaGem /> Premium & Limits
        </button>
        <button onClick={() => setActiveTab('service')} className={`pb-2 px-4 font-medium flex items-center gap-2 ${activeTab === 'service' ? 'border-b-2 border-primary text-primary' : 'text-gray-500'}`}>
          <FaTools /> Service & Payouts
        </button>
        <button onClick={() => setActiveTab('sms')} className={`pb-2 px-4 font-medium flex items-center gap-2 ${activeTab === 'sms' ? 'border-b-2 border-primary text-primary' : 'text-gray-500'}`}>
          <FaCommentDots /> SMS & Email
        </button>
        <button onClick={() => setActiveTab('site')} className={`pb-2 px-4 font-medium flex items-center gap-2 ${activeTab === 'site' ? 'border-b-2 border-primary text-primary' : 'text-gray-500'}`}>
          <FaInfoCircle /> Site Info
        </button>
        <button onClick={() => setActiveTab('media')} className={`pb-2 px-4 font-medium flex items-center gap-2 ${activeTab === 'media' ? 'border-b-2 border-primary text-primary' : 'text-gray-500'}`}>
          <FaCamera /> Media
        </button>
        <button onClick={() => setActiveTab('health')} className={`pb-2 px-4 font-medium flex items-center gap-2 ${activeTab === 'health' ? 'border-b-2 border-primary text-primary' : 'text-gray-500'}`}>
          <FaHeartbeat /> Health
        </button>
        <button onClick={() => setActiveTab('logs')} className={`pb-2 px-4 font-medium flex items-center gap-2 ${activeTab === 'logs' ? 'border-b-2 border-primary text-primary' : 'text-gray-500'}`}>
          <FaListAlt /> Logs
        </button>
      </div>

      {/* --- TAB 1: BANNERS --- */}
      {activeTab === 'banners' && (
          <div>
              <div className="flex justify-between items-center mb-4">
                  <h2 className="text-lg font-bold text-gray-700">Marketing Banners</h2>
                  <button onClick={() => handleOpenBannerModal()} className="bg-primary text-white px-4 py-2 rounded font-bold flex items-center gap-2 hover:bg-primary-dark">
                      <FaPlus /> Schedule New
                  </button>
              </div>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  {banners.length === 0 ? (
                      <div className="p-8 text-center text-gray-500">No banners scheduled.</div>
                  ) : (
                      <div className="divide-y divide-gray-100">
                          {banners.map(banner => (
                              <div key={banner.id} className="p-4 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 hover:bg-gray-50">
                                  <div className="flex-grow">
                                      <div className="flex items-center gap-3 mb-1">
                                          {getBannerStatus(banner)}
                                          <span className="font-bold text-gray-800">{banner.text}</span>
                                      </div>
                                      <div className="text-xs text-gray-500 flex flex-wrap gap-3">
                                          <span className="flex items-center gap-1"><FaCalendarAlt /> {new Date(banner.start_date).toLocaleString()} - {new Date(banner.end_date).toLocaleString()}</span>
                                          <span className="flex items-center gap-1"><FaRunning /> {banner.animation_mode === 'slide' ? `Scrolling (${banner.animation_speed}s)` : 'Static'}</span>
                                          {banner.link_url && <span className="flex items-center gap-1 text-blue-600"><FaLink /> Linked</span>}
                                      </div>
                                  </div>
                                  
                                  <button 
                                      onClick={() => handleOpenBannerModal(banner)}
                                      className="px-3 py-1 rounded text-xs font-bold hidden md:block truncate max-w-[200px] shadow-sm hover:opacity-90 transition-opacity"
                                      style={{ backgroundColor: banner.background_color, color: banner.text_color }}
                                      title="Click to Edit"
                                  >
                                      Preview Style (Edit)
                                  </button>

                                  <div className="flex gap-2">
                                      <button onClick={() => toggleBannerStatus(banner)} className={`p-2 rounded ${banner.is_active ? 'text-yellow-600 hover:bg-yellow-100' : 'text-green-600 hover:bg-green-100'}`} title={banner.is_active ? "Disable" : "Enable"}>
                                          {banner.is_active ? <FaTimes /> : <FaCheck />}
                                      </button>
                                      <button onClick={() => handleOpenBannerModal(banner)} className="p-2 text-blue-600 hover:bg-blue-100 rounded"><FaEdit /></button>
                                      <button onClick={() => handleDeleteBanner(banner.id)} className="p-2 text-red-600 hover:bg-red-100 rounded"><FaTrash /></button>
                                  </div>
                              </div>
                          ))}
                      </div>
                  )}
              </div>
          </div>
      )}

      {/* --- TAB 2: PRICING --- */}
      {activeTab === 'pricing' && (
        <div className="bg-white p-6 rounded-lg shadow-sm max-w-5xl mx-auto">
          <h2 className="text-lg font-bold text-gray-800 mb-4">Service & Logistics Pricing (NGN)</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-4 bg-blue-50 rounded border border-blue-100">
              <h3 className="font-bold text-blue-800 mb-3">Mechanic Travel</h3>
              <div className="space-y-3">
                <div><label className="block text-xs font-bold text-gray-600 mb-1">Base Fee</label><input type="number" name="mechanic_base_fee" value={pricing.mechanic_base_fee || ''} onChange={handleConfigChange(setPricing)} className="w-full p-2 border rounded" /></div>
                <div><label className="block text-xs font-bold text-gray-600 mb-1">Cost Per KM</label><input type="number" name="mechanic_cost_per_km" value={pricing.mechanic_cost_per_km || ''} onChange={handleConfigChange(setPricing)} className="w-full p-2 border rounded" /></div>
              </div>
            </div>
            <div className="p-4 bg-red-50 rounded border border-red-100">
              <h3 className="font-bold text-red-800 mb-3">Towing Service</h3>
              <div className="space-y-3">
                <div><label className="block text-xs font-bold text-gray-600 mb-1">Base Fee</label><input type="number" name="towing_base_fee" value={pricing.towing_base_fee || ''} onChange={handleConfigChange(setPricing)} className="w-full p-2 border rounded" /></div>
                <div><label className="block text-xs font-bold text-gray-600 mb-1">Cost Per KM</label><input type="number" name="towing_cost_per_km" value={pricing.towing_cost_per_km || ''} onChange={handleConfigChange(setPricing)} className="w-full p-2 border rounded" /></div>
              </div>
            </div>
            <div className="p-4 bg-gray-50 rounded border border-gray-200">
              <h3 className="font-bold text-gray-800 mb-3">Standard Fees</h3>
              <div className="space-y-3">
                <div><label className="block text-xs font-bold text-gray-600 mb-1">Diagnosis Fee</label><input type="number" name="diagnosis_fee" value={pricing.diagnosis_fee || ''} onChange={handleConfigChange(setPricing)} className="w-full p-2 border rounded" /></div>
                <div><label className="block text-xs font-bold text-gray-600 mb-1">Inspection Fee</label><input type="number" name="inspection_fee" value={pricing.inspection_fee || ''} onChange={handleConfigChange(setPricing)} className="w-full p-2 border rounded" /></div>
              </div>
            </div>
            <div className="p-4 bg-purple-50 rounded border border-purple-100">
              <h3 className="font-bold text-purple-800 mb-3">Commissions (%)</h3>
              <div className="space-y-3">
                  <div><label className="block text-xs font-bold text-gray-600 mb-1">Referral</label><input type="number" step="0.1" name="referral_commission_percentage" value={pricing.referral_commission_percentage || ''} onChange={handleConfigChange(setPricing)} className="w-full p-2 border rounded" /></div>
                  <div><label className="block text-xs font-bold text-gray-600 mb-1">Platform</label><input type="number" step="0.1" name="platform_commission_percent" value={pricing.platform_commission_percent || ''} onChange={handleConfigChange(setPricing)} className="w-full p-2 border rounded" /></div>
              </div>
            </div>
            <div className="p-4 bg-green-50 rounded border border-green-100 md:col-span-2">
              <h3 className="font-bold text-green-800 mb-3">Product Delivery</h3>
              <div className="grid grid-cols-2 gap-2">
                <div><label className="block text-[10px] font-bold text-gray-600">Bike Base</label><input type="number" name="bike_delivery_base" value={pricing.bike_delivery_base || ''} onChange={handleConfigChange(setPricing)} className="w-full p-2 border rounded text-sm" /></div>
                <div><label className="block text-[10px] font-bold text-gray-600">Bike / KM</label><input type="number" name="bike_delivery_per_km" value={pricing.bike_delivery_per_km || ''} onChange={handleConfigChange(setPricing)} className="w-full p-2 border rounded text-sm" /></div>
                <div><label className="block text-[10px] font-bold text-gray-600">Van Base</label><input type="number" name="van_delivery_base" value={pricing.van_delivery_base || ''} onChange={handleConfigChange(setPricing)} className="w-full p-2 border rounded text-sm" /></div>
                <div><label className="block text-[10px] font-bold text-gray-600">Van / KM</label><input type="number" name="van_delivery_per_km" value={pricing.van_delivery_per_km || ''} onChange={handleConfigChange(setPricing)} className="w-full p-2 border rounded text-sm" /></div>
                <div className="col-span-2 mt-2"><label className="block text-xs font-bold text-green-800">Shipping Deadline (Hours)</label><input type="number" name="seller_shipping_deadline_hours" value={pricing.seller_shipping_deadline_hours || 48} onChange={handleConfigChange(setPricing)} className="w-full p-2 border rounded text-sm" /></div>
              </div>
            </div>
          </div>
          <div className="mt-6 flex justify-end">
            <button onClick={() => saveConfig('pricing', pricing)} disabled={saving} className="bg-primary hover:bg-primary-dark text-white font-bold py-3 px-6 rounded flex items-center gap-2">
              {saving ? <FaSpinner className="animate-spin" /> : <FaSave />} Save Pricing
            </button>
          </div>
        </div>
      )}

      {/* --- TAB 3: PREMIUM & LIMITS --- */}
      {activeTab === 'premium' && (
          <div className="bg-white p-6 rounded-lg shadow-sm max-w-5xl mx-auto">
              <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <FaGem className="text-purple-600" /> Premium Features & Economy
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="bg-red-50 p-5 rounded-xl border border-red-200 md:col-span-2">
                      <h3 className="font-bold text-red-800 mb-3 flex items-center gap-2"><FaBan /> Listing Limits (Spam Stopper)</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div><label className="block text-xs font-bold text-gray-600 mb-1">Max Free Listings</label><input type="number" name="max_free_listings" value={premiumConfig.max_free_listings ?? 20} onChange={handleConfigChange(setPremiumConfig)} className="w-full p-2 border rounded" /></div>
                          <div><label className="block text-xs font-bold text-gray-600 mb-1">Price Per Extra Listing (NGN)</label><input type="number" name="price_per_listing" value={premiumConfig.price_per_listing || 1000} onChange={handleConfigChange(setPremiumConfig)} className="w-full p-2 border rounded" /></div>
                      </div>
                  </div>
                  <div className="bg-yellow-50 p-5 rounded-xl border border-yellow-200 md:col-span-2">
                      <h3 className="font-bold text-yellow-800 mb-3 flex items-center gap-2"><FaCamera /> Snap & Save Configuration</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" name="camera_feature_enabled" checked={premiumConfig.camera_feature_enabled || false} onChange={handleConfigChange(setPremiumConfig)} className="h-5 w-5 text-yellow-600 rounded" /><span className="text-sm font-bold text-gray-700">Enable Snap & Save</span></label>
                          <div><label className="block text-xs font-bold text-gray-600 mb-1">Free Snaps Limit</label><input type="number" name="free_camera_snaps" value={premiumConfig.free_camera_snaps || 0} onChange={handleConfigChange(setPremiumConfig)} className="w-full p-2 border rounded" /></div>
                          <div><label className="block text-xs font-bold text-gray-600 mb-1">Unlock Price (NGN)</label><input type="number" name="snap_and_save_unlock_price" value={premiumConfig.snap_and_save_unlock_price || 0} onChange={handleConfigChange(setPremiumConfig)} className="w-full p-2 border rounded" /></div>
                      </div>
                  </div>
                  <div className="bg-purple-50 p-5 rounded-xl border border-purple-200">
                      <h3 className="font-bold text-purple-800 mb-3">Listing Image Limits</h3>
                      <div className="grid grid-cols-2 gap-4">
                          <div><label className="block text-xs font-bold text-gray-600 mb-1">Free User Limit</label><input type="number" name="free_image_limit" value={premiumConfig.free_image_limit || 0} onChange={handleConfigChange(setPremiumConfig)} className="w-full p-2 border rounded" /></div>
                          <div><label className="block text-xs font-bold text-gray-600 mb-1">Premium Limit</label><input type="number" name="premium_image_limit" value={premiumConfig.premium_image_limit || 0} onChange={handleConfigChange(setPremiumConfig)} className="w-full p-2 border rounded" /></div>
                      </div>
                  </div>
                  <div className="bg-blue-50 p-5 rounded-xl border border-blue-200">
                      <h3 className="font-bold text-blue-800 mb-3">AI Mechanic Subscription</h3>
                      <div className="space-y-3">
                          <div><label className="block text-xs font-bold text-gray-600 mb-1">Monthly Price (NGN)</label><input type="number" name="ai_mechanic_monthly_price" value={premiumConfig.ai_mechanic_monthly_price || 0} onChange={handleConfigChange(setPremiumConfig)} className="w-full p-2 border rounded" /></div>
                          <div className="grid grid-cols-2 gap-4">
                              <div><label className="block text-xs font-bold text-gray-600 mb-1">Free Daily Chats</label><input type="number" name="free_chat_limit_daily" value={premiumConfig.free_chat_limit_daily || 0} onChange={handleConfigChange(setPremiumConfig)} className="w-full p-2 border rounded" /></div>
                              <div><label className="block text-xs font-bold text-gray-600 mb-1">Premium Vision Limit</label><input type="number" name="premium_vision_limit_monthly" value={premiumConfig.premium_vision_limit_monthly || 0} onChange={handleConfigChange(setPremiumConfig)} className="w-full p-2 border rounded" /></div>
                          </div>
                      </div>
                  </div>
                  <div className="bg-red-50 p-5 rounded-xl border border-red-200 md:col-span-2">
                      <h3 className="font-bold text-red-800 mb-3 flex items-center gap-2"><FaRocket /> Boost / Promotion Unit Pricing (NGN)</h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div><label className="block text-xs font-bold text-gray-600 mb-1">1 Hour</label><input type="number" name="boost_price_1h" value={premiumConfig.boost_price_1h || 0} onChange={handleConfigChange(setPremiumConfig)} className="w-full p-2 border rounded" /></div>
                          <div><label className="block text-xs font-bold text-gray-600 mb-1">1 Day</label><input type="number" name="boost_price_24h" value={premiumConfig.boost_price_24h || 0} onChange={handleConfigChange(setPremiumConfig)} className="w-full p-2 border rounded" /></div>
                          <div><label className="block text-xs font-bold text-gray-600 mb-1">1 Week</label><input type="number" name="boost_price_7d" value={premiumConfig.boost_price_7d || 0} onChange={handleConfigChange(setPremiumConfig)} className="w-full p-2 border rounded" /></div>
                          <div><label className="block text-xs font-bold text-gray-600 mb-1">1 Month</label><input type="number" name="boost_price_30d" value={premiumConfig.boost_price_30d || 0} onChange={handleConfigChange(setPremiumConfig)} className="w-full p-2 border rounded" /></div>
                      </div>
                  </div>
              </div>
              <div className="mt-8 flex justify-end gap-3">
                  <button onClick={async () => { if(window.confirm("Reset to defaults?")) { await settingsApi.resetSettings('premium_config'); fetchData(); } }} className="bg-white border border-red-300 text-red-600 hover:bg-red-50 font-bold py-3 px-6 rounded flex items-center gap-2"><FaUndo /> Reset</button>
                  <button onClick={() => saveConfig('premium_config', premiumConfig)} disabled={saving} className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-8 rounded flex items-center gap-2">{saving ? <FaSpinner className="animate-spin" /> : <FaSave />} Save Monetization</button>
              </div>
          </div>
      )}

      {/* --- TAB 4: SERVICE & PAYOUTS --- */}
      {activeTab === 'service' && (
          <div className="space-y-6 max-w-5xl mx-auto">
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                  <h2 className="text-lg font-bold text-gray-700 mb-4 flex items-center gap-2"><FaTools /> Service Follow-up & Returns</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-blue-50 p-6 rounded-xl border border-blue-100">
                      <div>
                          <h3 className="font-bold text-blue-800 mb-3">Post-Service Follow-up</h3>
                          <div className="space-y-3">
                              <div><label className="block text-xs font-bold text-gray-600 mb-1">Delay (Days)</label><input type="number" name="followup_delay_days" value={serviceConfig.followup_delay_days} onChange={handleConfigChange(setServiceConfig)} className="w-full p-2 border rounded" /></div>
                              <div><label className="block text-xs font-bold text-gray-600 mb-1">Channel</label><select name="followup_channel" value={serviceConfig.followup_channel} onChange={handleConfigChange(setServiceConfig)} className="w-full p-2 border rounded bg-white"><option value="email">Email</option><option value="sms">SMS</option></select></div>
                              <label className="flex items-center gap-2 mt-2"><input type="checkbox" name="enable_ai_followup" checked={serviceConfig.enable_ai_followup} onChange={handleConfigChange(setServiceConfig)} /><span className="text-sm font-bold text-gray-700">Use AI to write message</span></label>
                          </div>
                      </div>
                      <div>
                          <h3 className="font-bold text-blue-800 mb-3">Return Job (Warranty)</h3>
                          <div><label className="block text-xs font-bold text-gray-600 mb-1">Eligibility Window (Days)</label><input type="number" name="return_job_eligibility_days" value={serviceConfig.return_job_eligibility_days} onChange={handleConfigChange(setServiceConfig)} className="w-full p-2 border rounded" /></div>
                      </div>
                  </div>
                  <div className="mt-4 text-right"><button onClick={() => saveConfig('service_config', serviceConfig)} disabled={saving} className="bg-blue-600 text-white px-4 py-2 rounded font-bold hover:bg-blue-700">Save Service Config</button></div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                  <div className="flex justify-between items-center mb-4">
                      <h2 className="text-lg font-bold text-gray-700 flex items-center gap-2"><FaMoneyCheckAlt /> Payout Automation</h2>
                      <div className={`px-3 py-1 rounded-full text-xs font-bold flex items-center gap-2 ${payoutConfig.auto_payout_enabled ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{payoutConfig.auto_payout_enabled ? <><FaPlay size={8} /> ACTIVE</> : <><FaPause size={8} /> PAUSED</>}</div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
                          <label className="flex items-center gap-4 cursor-pointer">
                              <div className={`w-14 h-8 rounded-full p-1 transition-colors border-2 ${payoutConfig.auto_payout_enabled ? 'bg-[#22c55e] border-[#16a34a]' : 'bg-[#6b7280] border-[#4b5563]'}`}>
                                  <input type="checkbox" name="auto_payout_enabled" checked={payoutConfig.auto_payout_enabled || false} onChange={handleConfigChange(setPayoutConfig)} className="hidden" />
                                  <div className={`bg-white w-5 h-5 rounded-full shadow-md transform transition-transform duration-300 ${payoutConfig.auto_payout_enabled ? 'translate-x-6' : ''}`}></div>
                              </div>
                              <div><span className="font-bold text-gray-800 block">Enable Auto-Payouts</span><span className="text-xs text-gray-500">If disabled, ALL requests require manual approval.</span></div>
                          </label>
                      </div>
                      <div className="space-y-4">
                          <div><label className="block text-sm font-bold text-gray-700 mb-1">Max Auto-Approve Amount (NGN)</label><input type="number" name="max_auto_amount" value={payoutConfig.max_auto_amount || ''} onChange={handleConfigChange(setPayoutConfig)} className="w-full p-3 border rounded-lg" /></div>
                          <div><label className="block text-sm font-bold text-gray-700 mb-1">Minimum Account Age (Days)</label><input type="number" name="min_account_age_days" value={payoutConfig.min_account_age_days || ''} onChange={handleConfigChange(setPayoutConfig)} className="w-full p-3 border rounded-lg" /></div>
                          <label className="flex items-center gap-3 p-3 rounded bg-blue-50 border border-blue-100 cursor-pointer"><input type="checkbox" name="require_kyc" checked={payoutConfig.require_kyc || false} onChange={handleConfigChange(setPayoutConfig)} className="h-5 w-5 text-blue-600 rounded" /><span className="text-sm font-bold text-blue-800">Require KYC Photo for Auto-Approval</span></label>
                      </div>
                  </div>
                  <div className="mt-4 text-right"><button onClick={() => saveConfig('payout_config', payoutConfig)} disabled={saving} className="bg-green-600 text-white px-4 py-2 rounded font-bold hover:bg-green-700">Save Payout Config</button></div>
              </div>
          </div>
      )}

      {/* --- TAB 5: SMS & EMAIL (UPDATED FOR GRANULAR CONTROL) --- */}
      {activeTab === 'sms' && (
          <div className="bg-white p-6 rounded-lg shadow-sm max-w-5xl mx-auto animate-fadeIn">
              <div className="flex justify-between items-center mb-6 border-b pb-4">
                  <div><h2 className="text-xl font-bold text-gray-800 flex items-center gap-2"><FaCommentDots className="text-blue-600" /> Notification Channels</h2></div>
                  <div className="bg-gray-100 px-4 py-2 rounded-lg border border-gray-200 text-right">
                      <p className="text-xs text-gray-500 uppercase font-bold">SMS Balance</p>
                      {loadingBalance ? <FaSpinner className="animate-spin text-primary" /> : smsBalance ? <div><span className="text-lg font-bold text-green-600">{smsBalance.currency} {smsBalance.balance?.toLocaleString()}</span><span className="text-xs text-gray-400 block">Provider: {smsBalance.provider}</span></div> : <span className="text-sm text-red-500">Error fetching balance</span>}
                  </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  {/* OTP Provider */}
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                      <h3 className="font-bold text-gray-700 mb-3">Active OTP Provider</h3>
                      <label className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all mb-2 ${smsConfig.active_otp_provider === 'termii' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                          <input type="radio" name="active_otp_provider" value="termii" checked={smsConfig.active_otp_provider === 'termii'} onChange={handleConfigChange(setSmsConfig)} className="h-5 w-5 text-blue-600" />
                          <div><span className="font-bold text-gray-800 block">Termii</span><span className="text-xs text-gray-500">Best for OTPs.</span></div>
                      </label>
                      <label className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${smsConfig.active_otp_provider === 'kudisms' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                          <input type="radio" name="active_otp_provider" value="kudisms" checked={smsConfig.active_otp_provider === 'kudisms'} onChange={handleConfigChange(setSmsConfig)} className="h-5 w-5 text-green-600" />
                          <div><span className="font-bold text-gray-800 block">KudiSMS</span><span className="text-xs text-gray-500">Self-managed OTPs.</span></div>
                      </label>
                  </div>

                  {/* SMS Provider */}
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                      <h3 className="font-bold text-gray-700 mb-3">Active SMS Provider</h3>
                      <label className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all mb-2 ${smsConfig.active_sms_provider === 'termii' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                          <input type="radio" name="active_sms_provider" value="termii" checked={smsConfig.active_sms_provider === 'termii'} onChange={handleConfigChange(setSmsConfig)} className="h-5 w-5 text-blue-600" />
                          <div><span className="font-bold text-gray-800 block">Termii</span><span className="text-xs text-gray-500">Standard SMS.</span></div>
                      </label>
                      <label className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${smsConfig.active_sms_provider === 'kudisms' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                          <input type="radio" name="active_sms_provider" value="kudisms" checked={smsConfig.active_sms_provider === 'kudisms'} onChange={handleConfigChange(setSmsConfig)} className="h-5 w-5 text-green-600" />
                          <div><span className="font-bold text-gray-800 block">KudiSMS</span><span className="text-xs text-gray-500">DND Refunds.</span></div>
                      </label>
                  </div>

                  {/* Email Provider */}
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                      <h3 className="font-bold text-gray-700 mb-3">Active Email Provider</h3>
                      <label className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all mb-2 ${smsConfig.active_email_provider === 'google' ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                          <input type="radio" name="active_email_provider" value="google" checked={smsConfig.active_email_provider === 'google'} onChange={handleConfigChange(setSmsConfig)} className="h-5 w-5 text-red-600" />
                          <div><span className="font-bold text-gray-800 block">Google SMTP</span><span className="text-xs text-gray-500">Standard Gmail.</span></div>
                      </label>
                      <label className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all mb-2 ${smsConfig.active_email_provider === 'kudisms' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                          <input type="radio" name="active_email_provider" value="kudisms" checked={smsConfig.active_email_provider === 'kudisms'} onChange={handleConfigChange(setSmsConfig)} className="h-5 w-5 text-green-600" />
                          <div><span className="font-bold text-gray-800 block">KudiSMS Email</span><span className="text-xs text-gray-500">Transactional API.</span></div>
                      </label>
                      <label className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${smsConfig.active_email_provider === 'termii' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                          <input type="radio" name="active_email_provider" value="termii" checked={smsConfig.active_email_provider === 'termii'} onChange={handleConfigChange(setSmsConfig)} className="h-5 w-5 text-blue-600" />
                          <div><span className="font-bold text-gray-800 block">Termii Email</span><span className="text-xs text-gray-500">Template API.</span></div>
                      </label>
                  </div>
              </div>

              <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
                  <div className="space-y-6">
                      <div className="p-4 bg-white rounded border border-blue-100">
                          <h3 className="font-bold text-blue-800 flex items-center gap-2"><FaCogs /> Termii Credentials</h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div><label className="block text-xs font-bold text-gray-600 mb-1">API Key</label><input type="text" name="termii_api_key" value={smsConfig.termii_api_key || ''} onChange={handleConfigChange(setSmsConfig)} className="w-full p-2 border rounded font-mono text-sm" /></div>
                              <div><label className="block text-xs font-bold text-gray-600 mb-1">Sender ID</label><input type="text" name="termii_sender_id" value={smsConfig.termii_sender_id || ''} onChange={handleConfigChange(setSmsConfig)} className="w-full p-2 border rounded" /></div>
                          </div>
                      </div>
                      <div className="p-4 bg-white rounded border border-green-100">
                          <h3 className="font-bold text-green-800 flex items-center gap-2"><FaCogs /> KudiSMS Credentials</h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div><label className="block text-xs font-bold text-gray-600 mb-1">API Key</label><input type="text" name="kudi_api_key" value={smsConfig.kudi_api_key || ''} onChange={handleConfigChange(setSmsConfig)} className="w-full p-2 border rounded font-mono text-sm" /></div>
                              <div><label className="block text-xs font-bold text-gray-600 mb-1">Sender ID</label><input type="text" name="kudi_sender_id" value={smsConfig.kudi_sender_id || ''} onChange={handleConfigChange(setSmsConfig)} className="w-full p-2 border rounded" /></div>
                          </div>
                      </div>
                  </div>
                  <div className="mt-6 flex justify-end"><button onClick={() => saveConfig('sms_config', smsConfig)} disabled={saving} className="bg-black text-white px-6 py-3 rounded-lg font-bold hover:opacity-80 flex items-center gap-2">{saving ? <FaSpinner className="animate-spin" /> : <FaSave />} Save & Switch</button></div>
              </div>
          </div>
      )}

      {/* --- TAB 6: SITE INFO --- */}
      {activeTab === 'site' && (
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 max-w-5xl mx-auto">
              <h2 className="text-lg font-bold text-gray-700 mb-4">Site Information</h2>
              <div className="space-y-4">
                  {Object.entries(siteContent).map(([key, value]) => (
                      <div key={key}>
                          <label className="block text-xs font-bold text-gray-500 uppercase mb-1">{key.replace(/_/g, ' ')}</label>
                          {key.includes('image') ? (
                              <div className="flex items-center gap-4">
                                  {value && <img src={value} alt="Preview" className="h-16 w-16 object-cover rounded border" />}
                                  <input type="file" onChange={(e) => handleImageUpload(e, 'site-content')} className="text-sm" />
                              </div>
                          ) : (
                              <input type="text" name={key} value={value} onChange={handleConfigChange(setSiteContent)} className="w-full p-2 border rounded" />
                          )}
                      </div>
                  ))}
              </div>
              <div className="mt-6 flex justify-end"><button onClick={() => saveConfig('site_content', siteContent)} disabled={saving} className="bg-blue-600 text-white px-6 py-2 rounded font-bold hover:bg-blue-700 flex items-center gap-2">{saving ? <FaSpinner className="animate-spin" /> : <FaSave />} Update Site Info</button></div>
          </div>
      )}

      {/* --- TAB 7: MEDIA --- */}
      {activeTab === 'media' && (
          <div className="bg-white p-6 rounded-lg shadow-sm max-w-5xl mx-auto">
              <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2"><FaCamera className="text-primary" /> Image Compression & Storage</h2>
              <p className="text-sm text-gray-600 mb-6">Set the compression quality (0.1 to 1.0). Lower values = smaller files.</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {Object.entries(mediaConfig).map(([key, value]) => (
                      <div key={key} className="bg-gray-50 p-4 rounded border border-gray-200">
                          <label className="block text-xs font-bold text-gray-600 mb-1 uppercase">{key.replace(/_/g, ' ')}</label>
                          <input type="range" name={key} min="0.1" max="1.0" step="0.05" value={value} onChange={handleConfigChange(setMediaConfig)} className="w-full" />
                          <div className="flex justify-between text-sm font-bold mt-1"><span>Quality:</span><span className="text-primary">{(value * 100).toFixed(0)}%</span></div>
                      </div>
                  ))}
              </div>
              <div className="mt-6 flex justify-end"><button onClick={() => saveConfig('media_config', mediaConfig)} disabled={saving} className="bg-primary hover:bg-primary-dark text-white font-bold py-3 px-6 rounded flex items-center gap-2">{saving ? <FaSpinner className="animate-spin" /> : <FaSave />} Save Compression Settings</button></div>
          </div>
      )}

      {/* --- TAB 8: HEALTH --- */}
      {activeTab === 'health' && (
          <div className="bg-white p-6 rounded-lg shadow-sm max-w-5xl mx-auto">
              <h2 className="text-lg font-bold text-gray-800 mb-4">System Health</h2>
              {health ? <pre className="bg-gray-900 text-green-400 p-4 rounded overflow-auto text-xs">{JSON.stringify(health, null, 2)}</pre> : <div className="text-center p-10">Loading Health Data...</div>}
          </div>
      )}

      {/* --- TAB 9: LOGS --- */}
      {activeTab === 'logs' && (
          <div className="bg-white p-6 rounded-lg shadow-sm max-w-5xl mx-auto">
              <h2 className="text-lg font-bold text-gray-800 mb-4">Audit Logs</h2>
              <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left">
                      <thead className="bg-gray-50 text-gray-600 uppercase text-xs"><tr><th className="p-3">Action</th><th className="p-3">Admin</th><th className="p-3">Details</th><th className="p-3">Date</th></tr></thead>
                      <tbody className="divide-y divide-gray-100">
                          {auditLogs.map((log, i) => (
                              <tr key={i} className="hover:bg-gray-50">
                                  <td className="p-3 font-bold">{log.action}</td>
                                  <td className="p-3">{log.admin_email}</td>
                                  <td className="p-3 text-gray-500 truncate max-w-xs">{JSON.stringify(log.details)}</td>
                                  <td className="p-3 text-gray-400">{new Date(log.created_at).toLocaleString()}</td>
                              </tr>
                          ))}
                      </tbody>
                  </table>
              </div>
          </div>
      )}

      {/* --- BANNER MODAL --- */}
      {isBannerModalOpen && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 p-4">
              <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col animate-fadeInUp">
                  <div className="flex justify-between items-center p-6 border-b">
                      <h3 className="text-xl font-bold">{editingBanner ? 'Edit Banner' : 'Schedule Banner'}</h3>
                      <button onClick={() => setIsBannerModalOpen(false)}><FaTimes /></button>
                  </div>
                  <form onSubmit={handleSaveBanner} className="p-6 overflow-y-auto space-y-4">
                      <div className="p-3 rounded text-center font-bold mb-4 shadow-sm" style={{ backgroundColor: bannerForm.background_color, color: bannerForm.text_color }}>{bannerForm.text || "Banner Preview Text"}</div>
                      <div><label className="block text-xs font-bold text-gray-600 mb-1">Banner Text</label><input value={bannerForm.text} onChange={handleBannerChange} name="text" required className="w-full p-2 border rounded" placeholder="e.g. 50% Off Sale!" /></div>
                      <div className="grid grid-cols-2 gap-4">
                          <div><label className="block text-xs font-bold text-gray-600 mb-1 flex items-center gap-1"><FaPalette /> Background</label><input type="color" name="background_color" value={bannerForm.background_color} onChange={handleBannerChange} className="w-full h-10 p-1 rounded border" /></div>
                          <div><label className="block text-xs font-bold text-gray-600 mb-1 flex items-center gap-1"><FaPalette /> Text Color</label><input type="color" name="text_color" value={bannerForm.text_color} onChange={handleBannerChange} className="w-full h-10 p-1 rounded border" /></div>
                      </div>
                      
                      {/* ANIMATION SETTINGS (SPEED SLIDER) */}
                      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                          <label className="block text-xs font-bold text-gray-600 mb-3 uppercase">Animation Settings</label>
                          <div className="flex items-center gap-4 mb-4">
                              <label className="flex items-center gap-2 cursor-pointer">
                                  <input type="radio" name="animation_mode" value="static" checked={bannerForm.animation_mode === 'static'} onChange={handleBannerChange} />
                                  <span className="text-sm">Static</span>
                              </label>
                              <label className="flex items-center gap-2 cursor-pointer">
                                  <input type="radio" name="animation_mode" value="slide" checked={bannerForm.animation_mode === 'slide'} onChange={handleBannerChange} />
                                  <span className="text-sm">Scrolling (Right to Left)</span>
                              </label>
                          </div>

                          {bannerForm.animation_mode === 'slide' && (
                              <div className="animate-fadeIn">
                                  <div className="flex justify-between items-center mb-1">
                                      <label className="text-xs font-bold text-gray-500">Scroll Speed (Duration)</label>
                                      <span className="text-xs font-bold text-primary">{bannerForm.animation_speed}s</span>
                                  </div>
                                  <input 
                                      type="range" 
                                      name="animation_speed" 
                                      min="5" 
                                      max="60" 
                                      step="1" 
                                      value={bannerForm.animation_speed} 
                                      onChange={handleBannerChange} 
                                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
                                  />
                                  <div className="flex justify-between text-[10px] text-gray-400 mt-1">
                                      <span>Fast (5s)</span>
                                      <span>Slow (60s)</span>
                                  </div>
                              </div>
                          )}
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                          <div>
                              <label className="block text-xs font-bold text-gray-600 mb-1">Start Date/Time</label>
                              <div className="flex gap-2">
                                  <input type="datetime-local" name="start_date" value={bannerForm.start_date} onChange={handleBannerChange} required className="w-full p-2 border rounded text-sm" />
                                  <button onClick={setBannerImmediately} className="bg-green-100 text-green-700 px-3 py-1 rounded text-xs font-bold hover:bg-green-200 flex items-center gap-1"><FaClock /> Now</button>
                              </div>
                          </div>
                          <div><label className="block text-xs font-bold text-gray-600 mb-1">End Date/Time</label><input type="datetime-local" name="end_date" value={bannerForm.end_date} onChange={handleBannerChange} required className="w-full p-2 border rounded text-sm" /></div>
                      </div>
                      <div><label className="block text-xs font-bold text-gray-600 mb-1">Link URL (Optional)</label><input name="link_url" value={bannerForm.link_url} onChange={handleBannerChange} className="w-full p-2 border rounded" placeholder="/parts or https://..." /></div>
                      <div className="flex items-center gap-2 pt-2"><input type="checkbox" name="is_active" checked={bannerForm.is_active} onChange={handleBannerChange} className="h-4 w-4 text-primary rounded" /><span className="text-sm font-bold text-gray-700">Enable Immediately</span></div>
                      <div className="flex justify-end gap-3 pt-4 border-t">
                          <button type="button" onClick={() => setIsBannerModalOpen(false)} className="px-4 py-2 text-gray-600">Cancel</button>
                          <button type="submit" disabled={saving} className="px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark flex items-center gap-2">{saving ? <FaSpinner className="animate-spin" /> : <FaSave />} Save Schedule</button>
                      </div>
                  </form>
              </div>
          </div>
      )}
    </div>
  );
}

export default AdminSettings;